import React from "react";
import { Button } from "@/components/ui/button";

type GalleryImage = {
  src: string;
  alt?: string;
};

interface GalleryGridProps {
  id?: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  images: (string | GalleryImage)[];
  columns?: {
    base?: number;
    md?: number;
    lg?: number;
  };
  className?: string;
  showOverlayButton?: boolean;
  overlayButtonText?: string;
}

export const GalleryGrid: React.FC<GalleryGridProps> = ({
  id,
  eyebrow = "",
  title = "",
  subtitle = "",
  images,
  columns = { base: 1, md: 2, lg: 4 },
  className = "",
  showOverlayButton = true,
  overlayButtonText = "View Image",
}) => {
  const resolved = images.map((img, index) =>
    typeof img === "string"
      ? { src: img, alt: `Gallery ${index + 1}` }
      : { src: img.src, alt: img.alt || `Gallery ${index + 1}` },
  );

  const gridClasses = [
    "grid",
    `grid-cols-${columns.base ?? 1}`,
    columns.md ? `md:grid-cols-${columns.md}` : "",
    columns.lg ? `lg:grid-cols-${columns.lg}` : "",
    "gap-6",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      id={id}
      className={`py-20 bg-orange-50 ${className}`}
      data-oid="wo226p5"
    >
      <div className="container mx-auto px-6" data-oid="ofw6v70">
        {(eyebrow || title || subtitle) && (
          <div className="text-center mb-16" data-oid="x2kcrih">
            {eyebrow && (
              <div
                className="flex items-center justify-center mb-6"
                data-oid="hsvqzl8"
              >
                <div
                  className="w-8 h-0.5 bg-orange-500 mr-4"
                  data-oid=".z1j-47"
                ></div>
                <p
                  className="text-sm font-bold tracking-[0.3em] text-orange-500"
                  data-oid="ro7x5no"
                >
                  {eyebrow}
                </p>
                <div
                  className="w-8 h-0.5 bg-orange-500 ml-4"
                  data-oid=".g2l9d3"
                ></div>
              </div>
            )}
            {title && (
              <h2
                className="font-serif text-5xl font-bold mb-4"
                data-oid=".ej8iak"
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-muted-foreground text-lg" data-oid="461s6_8">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className={gridClasses} data-oid="5dgwas-">
          {resolved.map((image, index) => (
            <div
              key={`${image.src}-${index}`}
              className="relative group overflow-hidden rounded-lg shadow-lg"
              data-oid="ud:2c:-"
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-500"
                loading={index === 0 ? "eager" : "lazy"}
                data-oid="xhvjkhy"
              />

              <div
                className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                data-oid="obs-.ys"
              />

              {showOverlayButton && (
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  data-oid="i65z93h"
                >
                  <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-black"
                    data-oid="t5k12h6"
                  >
                    {overlayButtonText}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GalleryGrid;
